import TnSnackBar, { SnackBarVariant } from '@components/snackbar/TnSnackBar';
import { createStyles, Grid, makeStyles, Paper, Theme, Tooltip } from '@material-ui/core';
import React, { useState } from 'react';

import auth from '../../_services/auth/Auth';
import { ReactComponent as MarketplaceIcon } from '../../static/_assets/marketplace_icon.svg';
import VoraLogo from '../../static/_assets/vora_logo.png';
import Loader from '../Loader';

interface Props {
  readonly logo: any;
  readonly name: string;
  readonly url: string;
  readonly workspaceId: string;
  readonly hasMarketplaceAccess?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 200,
      cursor: 'pointer',
      margin: 10,
      position: 'relative',
      boxShadow: '0px 2px 9px rgba(0, 0, 0, 0.1)',
      borderRadius: 4,
    },
    marketplaceBadge: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 28,
      height: 28,
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    card: {
      height: 130,
    },
    cardImage: {
      maxWidth: '180px',
      maxHeight: '80px',
      width: '100%',
      height: '100%',
    },
  }),
);

export default function DynamicClientCard({ logo, name, url, workspaceId, hasMarketplaceAccess }: Props) {
  const classes = useStyles();
  const [redirecting, setRedirecting] = useState(false);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackBarVariant, setSnackBarVariant] = useState<SnackBarVariant>('success');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  async function redirectToClientApp() {
    if (redirecting) {
      return;
    }

    setRedirecting(true);

    const clientTab = window.open('', '_blank');

    try {
      const { idToken, accessToken } = await auth.getWorkspaceTokens({ id: workspaceId, name, url });
      const redirectUrl = `${url}mpclientRedirect?idToken=${idToken}&accessToken=${accessToken}&workspaceId=${workspaceId}`;

      if (clientTab) {
        clientTab.location.href = redirectUrl;
      } else {
        window.location.href = redirectUrl;
      }
    } catch (error) {
      clientTab?.close();
      console.error('Error redirecting to client app:', error);
      setSnackBarOpen(true);
      setSnackBarVariant('error');
      setSnackBarMessage('Failed to redirect to client app. Please try again.');
    } finally {
      setRedirecting(false);
    }
  }

  function getClientLogo() {
    if (logo?.startsWith('http')) {
      return logo;
    }

    return VoraLogo;
  }

  return (
    <>
      <Paper className={classes.root} onClick={redirectToClientApp} data-testid={name + '_ClientCard'}>
        {hasMarketplaceAccess && (
          <Tooltip title="You have Marketplace Access">
            <div className={classes.marketplaceBadge} data-testid="marketplace-access-icon">
              <MarketplaceIcon />
            </div>
          </Tooltip>
        )}
        <Grid container justify={'center'} alignItems={'center'} className={classes.card}>
          <Grid item>
            {redirecting ? (
              <Grid container justify="center">
                <Loader />
              </Grid>
            ) : (
              <img src={getClientLogo()} alt={name} className={classes.cardImage} />
            )}
            <Grid container justify="center">
              {name}
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <TnSnackBar
        open={snackBarOpen}
        variant={snackBarVariant}
        message={snackBarMessage}
        handleClose={() => {
          setSnackBarOpen(false);
        }}
      />
    </>
  );
}
